import ActionButton from '@westwing/ui-kit/ActionButton';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import Separator from '@westwing/ui-kit/Separator';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
    StyledFormActions,
    StyledHeaderCentered,
    StyledPageContent,
    StyledPageTitle,
} from '../loginAndRegistration.style';
import LoginForm from '../LoginForm';
import testIds from '../testId.e2e';
import useTranslate from 'Hooks/useTranslate';
import useLocationChange from 'Hooks/useLocationChange';
import useApi from 'Hooks/useApi';

const StyledContainer = styled.div`
    ${forDesktop`
        padding: 40px 40px 20px 40px;
    `}
`;

export interface PartialLoginWrapperProps {
    onClearSession: () => void;
    forgotPasswordLink: string;
    redirectUrl: string;
    email: string;
    name: string;
    onShopWithAnotherAccount?: () => void;
    onForgotPasswordClick?: () => void;
}

const PartialLoginWrapper = ({
    onClearSession,
    forgotPasswordLink,
    redirectUrl,
    name,
    email: knownEmail,
    onShopWithAnotherAccount,
    onForgotPasswordClick,
}: PartialLoginWrapperProps): JSX.Element => {
    const { login } = useApi();
    const { tPlain } = useTranslate();
    const { locationChange } = useLocationChange();
    const [hasApiError, setHasApiError] = useState(false);
    const onLoginSubmit = useCallback(
        async formInput => {
            const { email, password } = formInput;
            await login(
                { email, password },
                false,
                () => {
                    setHasApiError(false);
                    if (redirectUrl) {
                        locationChange(redirectUrl);
                    }
                },
                () => {
                    setHasApiError(true);
                }
            );
        },
        [login, locationChange, redirectUrl]
    );

    const handleShopWithAnotherAccount = useCallback(() => {
        onClearSession();
        if (onShopWithAnotherAccount) {
            onShopWithAnotherAccount();
        }
    }, [onClearSession, onShopWithAnotherAccount]);

    return (
        <StyledContainer data-testid={testIds.partialLoginContainer}>
            <StyledHeaderCentered>
                <StyledPageTitle>
                    {name && name.length ? tPlain('Hello, %s', [name]) : tPlain('Welcome back')}
                </StyledPageTitle>
            </StyledHeaderCentered>
            <StyledPageContent>
                <LoginForm
                    knownEmail={knownEmail}
                    emailDisabled
                    forgotPasswordLink={forgotPasswordLink}
                    onForgotPasswordClick={onForgotPasswordClick}
                    onSubmit={onLoginSubmit}
                    hasApiError={hasApiError}
                    setHasApiError={setHasApiError}
                    description={tPlain('Please confirm your password for security reasons')}
                />
                <Separator>{tPlain('or')}</Separator>
                <StyledFormActions>
                    <ActionButton type="button" kind="secondary" onClick={handleShopWithAnotherAccount}>
                        {tPlain('Shop with another account')}
                    </ActionButton>
                </StyledFormActions>
            </StyledPageContent>
        </StyledContainer>
    );
};
export default PartialLoginWrapper;
